/**
 * Saving and reopening a spread proposal.
 *
 * The whole ProjectState is stored in the proposal's form_data JSON column, so
 * no schema change is needed to persist one. A `kind` marker sits alongside it
 * so the proposals list can tell a spread proposal from one saved by the older
 * form and send each row back to the flow that created it.
 *
 * Reopening runs the stored project through a normaliser, because a proposal
 * saved before a field existed (a machine type added later, say) would
 * otherwise hydrate with holes and price as NaN.
 */
import { proposalService, ProposalRecord } from '../services/proposalService';
import {
  ProjectState, Spread, MachineKey, MACHINE_KEYS, DEFAULT_ENABLED, ModuleKey,
} from './spreadSplitData';
import { DEFAULT_MT_OPTIONS } from './spreadMaterialData';
import { DEFAULT_PLATFORM_OPTIONS, DEFAULT_RETENTION_OPTIONS } from './spreadProjectOptions';
import { DEFAULT_SERVICES } from './spreadSplitData';

export const SPREAD_FORM_KIND = 'spread-project';
const SPREAD_FORM_VERSION = 1;

export interface SpreadFormData {
  kind: typeof SPREAD_FORM_KIND;
  version: number;
  project: ProjectState;
}

/** True when this proposal was saved by the spread flow. */
export function isSpreadProposal(formData: Record<string, unknown> | null | undefined): boolean {
  return !!formData && formData.kind === SPREAD_FORM_KIND;
}

export function toFormData(project: ProjectState): SpreadFormData {
  return { kind: SPREAD_FORM_KIND, version: SPREAD_FORM_VERSION, project };
}

const zeroMachines = (): Record<MachineKey, number> =>
  MACHINE_KEYS.reduce((acc, k) => ({ ...acc, [k]: 0 }), {} as Record<MachineKey, number>);

/** Fill in anything a stored spread predates, keeping the values it does carry. */
function normalizeSpread(stored: Partial<Spread>, index: number): Spread {
  return {
    id: stored.id ?? `s${index + 1}`,
    name: stored.name ?? `Spread ${index + 1}`,
    lengthValue: stored.lengthValue ?? 0,
    lengthUnit: stored.lengthUnit ?? 'mi',
    pipeLengthFt: stored.pipeLengthFt ?? 40,
    jointsOverride: stored.jointsOverride ?? null,
    throughputMode: stored.throughputMode ?? 'perDay',
    jointsPerDay: stored.jointsPerDay ?? 60,
    durationMonths: stored.durationMonths ?? 24,
    machines: { ...zeroMachines(), ...(stored.machines ?? {}) },
    mtMills: stored.mtMills ?? [],
    mtOptions: { ...DEFAULT_MT_OPTIONS, ...(stored.mtOptions ?? {}) },
    ndtSources: stored.ndtSources ?? [],
    coatingSources: stored.coatingSources ?? [],
    platformOptions: { ...DEFAULT_PLATFORM_OPTIONS, ...(stored.platformOptions ?? {}) },
    retentionOptions: { ...DEFAULT_RETENTION_OPTIONS, ...(stored.retentionOptions ?? {}) },
    enabled: { ...DEFAULT_ENABLED, ...(stored.enabled ?? {}) } as Record<ModuleKey, boolean>,
    overrides: stored.overrides ?? {},
  };
}

/** Rebuild a ProjectState from a saved proposal, or null if it is not one of ours. */
export function fromFormData(formData: Record<string, unknown> | null | undefined): ProjectState | null {
  if (!isSpreadProposal(formData)) return null;
  const stored = (formData as unknown as SpreadFormData).project;
  if (!stored || !Array.isArray(stored.spreads) || stored.spreads.length === 0) return null;
  return {
    customerName: stored.customerName ?? '',
    projectTitle: stored.projectTitle ?? '',
    refNumber: stored.refNumber ?? '',
    reporting: stored.reporting ?? 'spread',
    config: stored.config ?? {},
    // Engine defaults are re-fetched live on open, never trusted from storage.
    liveDefaults: undefined,
    services: { ...DEFAULT_SERVICES, ...(stored.services ?? {}) },
    spreads: stored.spreads.map(normalizeSpread),
  };
}

export interface SaveSpreadArgs {
  project: ProjectState;
  totalPrice: number;
  /** Set once the proposal exists. The save then overwrites that row in place,
   *  so saving on every step does not pile up a version per step. */
  proposalId?: string;
}

/**
 * Persist the project to RDS. The first save creates the proposal; later saves
 * overwrite it in place.
 */
export async function saveSpreadProposal({ project, totalPrice, proposalId }: SaveSpreadArgs): Promise<ProposalRecord> {
  if (!project.refNumber.trim()) {
    throw new Error('Add a reference number on the Project step before saving.');
  }
  const payload = {
    refNumber: project.refNumber.trim(),
    customerName: project.customerName,
    projectTitle: project.projectTitle,
    totalPrice,
    formData: toFormData(project),
  };
  return proposalId
    ? proposalService.overwriteProposal(proposalId, payload)
    : proposalService.saveProposal(payload);
}
